import { forceUpdate, h, Host, readTask } from "@stencil/core";
export class Tabs {
    constructor() {
        this.ids = -1;
        this.transitioning = false;
        this.tabsId = (++tabIds);
        this.tabs = [];
    }
    componentWillLoad() {
        this.initTabs();
        this.initSelect();
    }
    disconnectedCallback() {
        this.tabs.length = 0;
        this.selectedTab = undefined;
    }
    onTabAdded() {
        this.initTabs();
        this.select(0);
    }
    onTabRemoved() {
        readTask(() => {
            this.initTabs();
            this.select(0);
        });
    }
    onTabMutated() {
        forceUpdate(this.el);
    }
    onTabClicked(ev) {
        this.select(ev.detail);
    }
    async select(tabOrIndex) {
        const selectedTab = await this.getTab(tabOrIndex);
        if (!this.shouldSwitch(selectedTab)) {
            return false;
        }
        await this.setActive(selectedTab);
        this.tabSwitch();
        return true;
    }
    async getTab(tabOrIndex) {
        if (typeof tabOrIndex === 'number') {
            return this.tabs[tabOrIndex];
        }
        return tabOrIndex;
    }
    initTabs() {
        const tabs = this.tabs = Array.from(this.el.querySelectorAll('uvex-tab'));
        tabs.forEach(tab => {
            let id;
            if (tab.hasAttribute('id')) {
                id = tab.getAttribute('id').replace('tabpanel-', '');
            }
            else {
                id = `t-${this.tabsId}-${++this.ids}`;
            }
            tab.btnId = 'tab-' + id;
            tab.id = 'tabpanel-' + id;
        });
    }
    initSelect() {
        const tabs = this.tabs;
        // find pre-selected tabs
        const selectedTab = tabs.find(t => t.selected) || tabs[0];
        // reset all tabs none is selected
        for (const tab of tabs) {
            if (tab !== selectedTab) {
                tab.selected = false;
            }
        }
        this.selectedTab = selectedTab;
        if (selectedTab) {
            selectedTab.selected = true;
        }
    }
    setActive(selectedTab) {
        if (this.transitioning) {
            return Promise.reject('transitioning already happening');
        }
        // Reset rest of tabs
        for (const tab of this.tabs) {
            if (selectedTab !== tab) {
                tab.selected = false;
            }
        }
        this.transitioning = true;
        this.leavingTab = this.selectedTab;
        this.selectedTab = selectedTab;
        selectedTab.selected = true;
    }
    tabSwitch() {
        const selectedTab = this.selectedTab;
        const leavingTab = this.leavingTab;
        this.leavingTab = undefined;
        this.transitioning = false;
        if (!selectedTab) {
            return;
        }
        selectedTab.selected = true;
        if (leavingTab !== selectedTab) {
            if (leavingTab) {
                leavingTab.selected = false;
            }
            this.uvexChange.emit({ tab: selectedTab });
            this.uvexNavDidChange.emit();
        }
    }
    shouldSwitch(selectedTab) {
        const leavingTab = this.selectedTab;
        return selectedTab !== undefined && selectedTab !== leavingTab && !this.transitioning;
    }
    render() {
        return h(Host, { key: 'd3c8fe25863cac0b24f0237537cc2dbe6328c335' }, h("uvex-tabbar", { key: '7786e8b1e6f22050e30074e53c60aa0338808073', tabs: this.tabs, selectedTab: this.selectedTab }), h("div", { key: 'bc4bcac5902fa5e358f731dbea405c345b7f0ed8', class: "tabs-inner" }, h("slot", { key: 'ea92aecf870644c8d44e1c03bf4f9e5d24beb770' })));
    }
    static get is() { return "uvex-tabs"; }
    static get states() {
        return {
            "tabs": {},
            "selectedTab": {}
        };
    }
    static get events() {
        return [{
                "method": "uvexChange",
                "name": "uvexChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the tab changes."
                },
                "complexType": {
                    "original": "{ tab: HTMLUvexTabElement }",
                    "resolved": "{ tab: HTMLUvexTabElement; }",
                    "references": {
                        "HTMLUvexTabElement": {
                            "location": "global",
                            "id": "global::HTMLUvexTabElement"
                        }
                    }
                }
            }, {
                "method": "uvexNavDidChange",
                "name": "uvexNavDidChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the navigation has finished transitioning to a new component."
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "select": {
                "complexType": {
                    "signature": "(tabOrIndex: number | HTMLUvexTabElement) => Promise<boolean>",
                    "parameters": [{
                            "name": "tabOrIndex",
                            "type": "any",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLUvexTabElement": {
                            "location": "global",
                            "id": "global::HTMLUvexTabElement"
                        }
                    },
                    "return": "Promise<boolean>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "getTab": {
                "complexType": {
                    "signature": "(tabOrIndex: number | HTMLUvexTabElement) => Promise<HTMLUvexTabElement | undefined>",
                    "parameters": [{
                            "name": "tabOrIndex",
                            "type": "any",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLUvexTabElement": {
                            "location": "global",
                            "id": "global::HTMLUvexTabElement"
                        }
                    },
                    "return": "Promise<any>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "uvexTabAdded",
                "method": "onTabAdded",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uvexTabRemoved",
                "method": "onTabRemoved",
                "target": "document",
                "capture": false,
                "passive": true
            }, {
                "name": "uvexTabMutated",
                "method": "onTabMutated",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "uvexTabbarClick",
                "method": "onTabClicked",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
let tabIds = -1;
//# sourceMappingURL=tabs.js.map
